import React, { useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';

import { DictionaryData, isUseTranslationData, tFunction, useTranslation } from 'app/lang';

import styles from './Tabs.module.scss';

// ---

export enum TabsSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

const sizeClassMap: Record<TabsSize, string> = {
  [TabsSize.SMALL]: styles.small,
  [TabsSize.MEDIUM]: styles.medium,
  [TabsSize.LARGE]: styles.large,
};

// ---

type DefaultTab <Id> = {
  id: Id;
  label: JSX.Element | string | DictionaryData;
  disabled?: boolean;
  title?: string;
  active?: boolean;
  'data-test'?: string;
  dataGaValues?: Record<string, string | number>;
};

type ButtonTab<Id> = DefaultTab<Id>;
type LinkTab<Id> = DefaultTab<Id> & {
  to: string;
  exact?: boolean;
};

export type TabByType<Id> = ButtonTab<Id> | LinkTab<Id>;

type TabsByType<Id> = {
  type: 'button';
  tabs: ButtonTab<Id>[];
} | {
  type: 'link';
  tabs: LinkTab<Id>[];
};

type TabsProps<TabId> = TabsByType<TabId> & {
  activeTabId?: TabId;
  onChange?: (key: TabId, tab: TabByType<TabId>) => void;
  size?: TabsSize;
  className?: string;
  disableActiveTab?: boolean;
  'data-test'?: string;
};

/*
 * activeTabId - Устанавливает активный таб, вместо этого ключа можно использовать свойство active.
 * В таком случае, оно должно быть у каждого таба: { id: 1, label: '', active: true }
 *
 * type - при варианте 'link' убедитесь, что компонент <Tabs /> имеет обертку <Router>
 */
export function Tabs<TabId extends string | number>({
  activeTabId,
  tabs,
  onChange,
  type,
  className,
  size = TabsSize.SMALL,
  disableActiveTab = true,
  ...props
}: TabsProps<TabId>): JSX.Element {
  const { t } = useTranslation();
  const clickOnTabHandler = useCallback((id: TabId, tab: ButtonTab<TabId>) => () => {
    onChange && onChange(id, tab);
  }, [onChange]);

  const clickOnLinkHandler = useCallback((
    id: TabId,
    tab: LinkTab<TabId>,
    disabled?: boolean,
  ) => (
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        event.preventDefault();
        return;
      }

      onChange && onChange(id, tab);
    }
  ), [onChange]);

  const tabsWithType = tabs as (ButtonTab<TabId> | LinkTab<TabId>)[];

  return (
    <div data-test={classNames(props['data-test'], 'tabs-container')}
         className={classNames(styles.tabs, className)}>
      {tabsWithType.map((tab) => {
        const active = tab.active || activeTabId === tab.id;
        const disabled = (disableActiveTab && active) || tab.disabled;

        const _className = classNames(styles.tab, sizeClassMap[size], {
          [styles.active]: active,
          [styles.disabled]: disabled,
        });

        const defaultProps = {
          'title': tab.title,
          'className': _className,
          'data-active': active,
        };

        if (type === 'link') {
          const linkTab = tab as LinkTab<TabId>;
          return (
            <NavLink {...defaultProps}
                     key={linkTab.id}
                     to={linkTab.to}
                     exact={linkTab.exact}
                     data-test={classNames(`tabs-tabLink tab-${linkTab.id}`, tab['data-test'])}
                     {...tab.dataGaValues}
                     onClick={clickOnLinkHandler(linkTab.id, linkTab, disabled)}>
              {getTabLabel(t, linkTab.label)}
            </NavLink>
          );
        }

        return (
          <button {...defaultProps}
                  key={tab.id}
                  type="button"
                  disabled={disabled}
                  data-test={classNames(`tabs-tabButton tab-${tab.id}`, tab['data-test'])}
                  {...tab.dataGaValues}
                  onClick={clickOnTabHandler(tab.id, tab)}>
            {getTabLabel(t, tab.label)}
          </button>
        );
      })}
    </div>
  );
}

function getTabLabel(t:tFunction, label: JSX.Element | string | DictionaryData) {
  if (isUseTranslationData(label)) {
    return t(label);
  }
  return label;
}
